import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { SeverityBadge } from '@/components/shared/UiElements';
import { CLAUDE_SECTION, CLAUDE_DEMO } from '@/pages/homeLandingContent';

/* "Works inside Claude" (owner, 2026-10-08), from the Claude Design handoff,
 * rendered on the homepage only when `fresh`, after the two products.
 *
 * The handoff's values are kept except where tokens.css overrules them:
 *   #FF5C2B (stale accent) -> --accent for fills, --accent-text for hover text;
 *   CRITICAL chips -> SeverityBadge (meter + word, the Critical fill and ink);
 *   active tab text #B3320A (danger only) -> the Major pair;
 *   the "+13 pts" chip is dropped: a stated delta is barred (see CLAUDE_DEMO);
 *   44px h2 -> the system .uxa-h2; tabs 34px -> 44px targets;
 *   the mock Approve / Not now are text, not buttons that do nothing;
 *   the CTA goes to /claude/ instead of #setup;
 *   small grey text (--ink-3, 3.4:1 measured) -> --ink-2, for AA. The large
 *   "58" keeps --ink-3: large text needs 3:1.
 * The section's own dotted ground is dropped: the page already draws it.
 *
 * Nothing here is a claim of its own: the copy and the card's tools, modes and
 * fields live in homeLandingContent.js, sourced there. */

const muted = { color: 'var(--ink-3)' };
const card = {
  background: 'var(--surface-white)', border: '1px solid var(--border-2)', borderRadius: 16,
  padding: '18px 20px', boxShadow: '0 1px 2px rgba(0,0,0,.06), 0 2px 6px rgba(0,0,0,.04)',
};
const stat = { background: 'var(--surface-panel)', borderRadius: 12, padding: 12 };
const pill = {
  display: 'inline-flex', alignItems: 'center', gap: 8, height: 34, padding: '0 14px',
  borderRadius: 100, fontSize: 14, fontWeight: 600,
};
const dot = { width: 7, height: 7, borderRadius: '50%', flex: 'none' };

function Result({ tab }) {
  const d = CLAUDE_DEMO;
  if (tab === 'list') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        {d.list.map((r) => (
          <div key={r.page} style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', gap: 16, alignItems: 'center', padding: '11px 0', borderTop: '1px solid var(--surface-sand-deep)', fontSize: 14 }}>
            <span style={{ fontWeight: 600 }}>{r.page}</span>
            <span style={{ color: 'var(--ink-2)' }}>{r.when}</span>
            <span style={{ fontWeight: 700, minWidth: 28, textAlign: 'right' }}>{r.score}</span>
          </div>
        ))}
      </div>
    );
  }
  if (tab === 'read') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {d.findings.map((f) => (
          <div key={f} style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
            <span style={{ flex: 'none' }}><SeverityBadge severity="critical" label="Critical" /></span>
            <span style={{ fontSize: 14, lineHeight: 1.5 }}>{f}</span>
          </div>
        ))}
      </div>
    );
  }
  if (tab === 'compare') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 14 }}>
          <span style={{ fontSize: 34, fontWeight: 700, letterSpacing: -1, ...muted }}>{d.compare.from}</span>
          <span style={{ fontSize: 18, ...muted }} aria-hidden="true">→</span>
          <span style={{ fontSize: 34, fontWeight: 700, letterSpacing: -1 }}>{d.compare.to}</span>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
          {d.compare.stats.map((s) => (
            <div key={s.label} style={stat}>
              <div style={{ fontSize: 20, fontWeight: 700 }}>{s.value}</div>
              <div style={{ fontSize: 12, color: 'var(--ink-2)' }}>{s.label}</div>
            </div>
          ))}
        </div>
      </div>
    );
  }
  if (tab === 'free') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ fontSize: 15, fontWeight: 700 }}>{d.free.title}</div>
        <div style={{ fontSize: 14, lineHeight: 1.5, color: 'var(--ink-2)' }}>{d.free.meta}</div>
        <div style={{ height: 6, borderRadius: 3, background: 'var(--border-1)', overflow: 'hidden' }} aria-hidden="true">
          <div style={{ width: `${d.free.progress}%`, height: '100%', background: 'var(--accent)', borderRadius: 3 }} />
        </div>
      </div>
    );
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12, fontSize: 15, fontWeight: 700 }}>
        <span>{d.paid.title}</span>
        <span>{d.paid.price}</span>
      </div>
      <div style={{ fontSize: 14, lineHeight: 1.5, color: 'var(--ink-2)' }}>{d.paid.body}</div>
      <div style={{ display: 'flex', gap: 10, fontSize: 14, fontWeight: 500 }}>
        <span style={{ display: 'flex', alignItems: 'center', height: 40, padding: '0 20px', borderRadius: 100, background: 'var(--ink-strong)', color: 'var(--surface-white)' }}>{d.paid.approve}</span>
        <span style={{ display: 'flex', alignItems: 'center', height: 40, padding: '0 16px', borderRadius: 100, color: 'var(--ink-2)' }}>{d.paid.decline}</span>
      </div>
    </div>
  );
}

export default function HomeClaudeSection() {
  const [tab, setTab] = useState(CLAUDE_DEMO.initial);
  const active = CLAUDE_DEMO.tabs.find((t) => t.key === tab);
  const [claudeLabel, chatgptLabel] = CLAUDE_SECTION.labels;

  return (
    <section className="uxa-claude" data-testid="home-claude" aria-labelledby="home-claude-title">
      <div className="uxa-claude__grid">
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
          <span className="section-label">{CLAUDE_SECTION.eyebrow}</span>
          <h2 id="home-claude-title" className="uxa-h2" style={{ color: 'var(--ink-1)', margin: '18px 0 0', textWrap: 'balance' }}>
            {CLAUDE_SECTION.h2}
          </h2>
          <p style={{ fontSize: 18, lineHeight: 1.6, color: 'var(--ink-2)', margin: '20px 0 0', maxWidth: 500, textWrap: 'pretty' }}>
            {CLAUDE_SECTION.body}
          </p>
          <ul style={{ listStyle: 'none', padding: 0, display: 'flex', flexWrap: 'wrap', gap: 10, margin: '28px 0 0' }}>
            <li style={{ ...pill, background: 'var(--success-bg)', color: 'var(--success-text)' }}>
              <span style={{ ...dot, background: 'var(--success-text)' }} aria-hidden="true" />{claudeLabel}
            </li>
            <li style={{ ...pill, background: 'var(--surface-sand-deep)', color: 'var(--ink-2)' }}>
              <span style={{ ...dot, border: '1.5px solid var(--ink-3)' }} aria-hidden="true" />{chatgptLabel}
            </li>
          </ul>
          <Link to={CLAUDE_SECTION.href} className="uxa-submit uxa-submit--auto" style={{ marginTop: 36 }}>
            {CLAUDE_SECTION.cta}
            <span className="uxa-submit-knob" aria-hidden="true"><ArrowRight className="w-4 h-4" /></span>
          </Link>
        </div>

        <div style={{ position: 'relative' }}>
          <div className="uxa-claude__glow" aria-hidden="true" />
          <div style={{ position: 'relative', background: 'var(--surface-white)', borderRadius: 24, boxShadow: '0 2px 6px rgba(0,0,0,.08), 0 24px 60px rgba(0,0,0,.1)', overflow: 'hidden' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '16px 22px', borderBottom: '1px solid var(--border-1)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 14, fontWeight: 600 }}>
                <span aria-hidden="true" style={{ width: '22px', height: '22px', borderRadius: 7, background: 'var(--accent)', color: 'var(--on-accent)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'var(--type-weight-mark)', fontSize: 10 }}>UX</span>
                UXAuditPro connector
              </div>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 500, color: 'var(--success-text)' }}>
                <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--success-text)' }} aria-hidden="true" />Connected
              </span>
            </div>

            <div aria-live="polite" style={{ padding: '24px 22px', minHeight: 360, display: 'flex', flexDirection: 'column', gap: 16, background: 'var(--surface-canvas)' }}>
              <div style={{ alignSelf: 'flex-end', maxWidth: '82%', padding: '12px 16px', borderRadius: '18px 18px 4px 18px', background: 'var(--ink-strong)', color: 'var(--surface-white)', fontSize: 15, lineHeight: 1.5 }}>
                {active.ask}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, fontWeight: 500, color: 'var(--ink-2)' }}>
                <span style={{ fontFamily: 'ui-monospace, Menlo, monospace', padding: '3px 8px', borderRadius: 6, background: 'var(--surface-sand-deep)', color: 'var(--ink-2)' }}>
                  uxauditpro · {active.tool}
                </span>
                <span>{active.mode}</span>
              </div>
              <div style={card}><Result tab={tab} /></div>
            </div>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, padding: '16px 22px', borderTop: '1px solid var(--border-1)', background: 'var(--surface-white)' }}>
              {CLAUDE_DEMO.tabs.map((t) => (
                <button
                  key={t.key}
                  type="button"
                  aria-pressed={t.key === tab}
                  onClick={() => setTab(t.key)}
                  className="uxa-claude__tab"
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
